import React, { useEffect, useMemo, useState } from "react"
import { CircularProgress, IconButton, Paper, TextField, Theme, Typography, makeStyles } from "@material-ui/core"
import { File, Paperclip, Send, Smile, X } from "react-feather"
import { useTranslation } from "react-i18next"
import { useChatMutations } from "../hooks/use-chat-mutations"
import { useFileUpload } from "../../storage/hooks/use-file-upload"
import { useFileMutations } from "../../storage/hooks/use-file-mutation"
import { MessageViewModel } from "../api/message"
import EmojiPicker from "./EmojiPicker"
import { useEmojiInsert } from "../hooks/use-emoji-insert"
import MentionPicker from "./MentionPicker"
import { useGetChatParticipantsQuery } from "../api"
import { ChatParticipantResponse } from "../api/chat-response"

// A mention starts at an "@" that opens a word and runs to the next space.
const MENTION_TRIGGER = /(?:^|\s)@([^\s@]*)$/
const MENTION_RESULTS = 6

type UploadedFile = {
  id: string
  file: File
  key: string
  uploading: boolean
  error?: boolean
  controller?: AbortController
}

const MessageComposer = ({
  chatId,
  senderId,
  orgId,
  replyTo,
  onCancelReply,
  onSent,
}: {
  chatId: string | null
  senderId: string
  orgId: string
  replyTo: MessageViewModel | null
  onCancelReply: () => void
  onSent: () => void
}) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")

  const [content, setContent] = useState("")
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [sending, setSending] = useState(false)
  const { inputRef, emojiAnchor, toggleEmojiPicker, closeEmojiPicker, insertEmoji } = useEmojiInsert(
    content,
    setContent
  )

  // The "@" that opened the picker, and what has been typed after it.
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null)
  const [activeMention, setActiveMention] = useState(0)
  const { data: participants = [] } = useGetChatParticipantsQuery({ chatId: chatId ?? "" }, { skip: !chatId })

  const mentionMatches = useMemo(() => {
    if (!mention) return []

    const query = mention.query.toLowerCase()

    return participants
      .filter((member) => member.userId !== senderId && member.name.toLowerCase().includes(query))
      .slice(0, MENTION_RESULTS)
  }, [mention, participants, senderId])

  const handleContentChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const next = event.target.value
    const caret = event.target.selectionStart ?? next.length
    const trigger = next.slice(0, caret).match(MENTION_TRIGGER)

    setContent(next)
    setMention(trigger ? { start: caret - trigger[1].length - 1, query: trigger[1] } : null)
    setActiveMention(0)
  }

  const insertMention = (member: ChatParticipantResponse) => {
    if (!mention) return

    const token = `@${member.name} `
    const caret = mention.start + token.length

    setContent(
      (current) => current.slice(0, mention.start) + token + current.slice(mention.start + 1 + mention.query.length)
    )
    setMention(null)

    // After the new value has rendered, or the re-render puts the caret back.
    requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.setSelectionRange(caret, caret)
    })
  }

  useEffect(() => {
    if (replyTo) inputRef.current?.focus()
  }, [replyTo, inputRef])

  const { sendMessage } = useChatMutations()
  const { uploadFilesToChat } = useFileUpload()
  const { deleteFile } = useFileMutations()

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files

    if (!selectedFiles) return

    const filesArray = Array.from(selectedFiles)

    event.target.value = ""

    const tempFiles: UploadedFile[] = filesArray.map((file) => ({
      id: "",
      file,
      key: "",
      uploading: true,
      controller: new AbortController(),
    }))

    setFiles((prev) => [...prev, ...tempFiles])

    try {
      const uploadedFiles = await uploadFilesToChat({
        files: filesArray,
        orgId,
        signal: tempFiles[0].controller?.signal,
      })

      setFiles((prev) => {
        const updated = [...prev]

        uploadedFiles.forEach((uploaded, index) => {
          const fileIndex = updated.findIndex((item) => item.file === filesArray[index])

          if (fileIndex !== -1) {
            updated[fileIndex] = {
              ...updated[fileIndex],
              id: uploaded.id,
              key: uploaded.key,
              uploading: false,
            }
          }
        })

        return updated
      })
    } catch (error) {
      if ((error as DOMException).name === "AbortError") {
        return
      }
      setFiles((prev) =>
        prev.map((item) =>
          filesArray.includes(item.file)
            ? {
                ...item,
                uploading: false,
                error: true,
              }
            : item
        )
      )
    }
  }

  const handleSend = async () => {
    const text = content.trim()

    const attachmentKeys = files.filter((file) => file.key).map((file) => file.key)

    if (!chatId || (!text && attachmentKeys.length === 0) || files.some((file) => file.uploading)) {
      return
    }

    try {
      setSending(true)

      const sent = await sendMessage({
        chatId,
        senderId,
        content: text,
        type: text ? "text" : "attachment",
        attachments: attachmentKeys,
        replyTo: replyTo?.id,
        // Whoever's name ends up in the text behind an "@", whether picked from the list or typed.
        mentions: participants.filter((member) => text.includes(`@${member.name}`)),
      })

      // A failed send already surfaced an error — keep the draft so it can be retried.
      if (!sent) return

      setContent("")
      setMention(null)
      setFiles([])
      closeEmojiPicker()
      onCancelReply()
      onSent()
    } finally {
      setSending(false)
    }
  }

  const removeFile = async (index: number) => {
    const file = files[index]

    // cancel active upload
    if (file.uploading && file.controller) {
      file.controller.abort()
    }

    // delete already uploaded storage file
    if (file.id) {
      await deleteFile(file.id)
    }

    setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index))
  }

  return (
    <Paper
      square
      className={classes.root}
      onKeyDown={(e) => {
        if (e.nativeEvent.isComposing) {
          return // dead key / IME composition in progress, don't submit
        }

        if (mentionMatches.length > 0) {
          if (e.key === "ArrowDown") {
            e.preventDefault()
            setActiveMention((index) => (index + 1) % mentionMatches.length)
            return
          }

          if (e.key === "ArrowUp") {
            e.preventDefault()
            setActiveMention((index) => (index - 1 + mentionMatches.length) % mentionMatches.length)
            return
          }

          // Enter picks the highlighted name rather than sending a half-typed mention.
          if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault()
            insertMention(mentionMatches[activeMention])
            return
          }
        }

        if (e.key === "Escape") {
          if (mention) setMention(null)
          else closeEmojiPicker()

          return
        }

        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault()
          handleSend()
        }
      }}
    >
      {replyTo && (
        <div className={classes.replyPreview}>
          <div className={classes.replyPreviewText}>
            <Typography className={classes.replyPreviewTitle}>
              {t("messageComposer.replyingToLabel", { name: replyTo.senderName })}
            </Typography>

            <Typography className={classes.replyPreviewContent}>{replyTo.content || t("attachmentLabel")}</Typography>
          </div>

          <IconButton size="small" aria-label={t("messageComposer.cancelReplyButtonLabel")} onClick={onCancelReply}>
            <X size={14} />
          </IconButton>
        </div>
      )}

      {files.length > 0 && (
        <div className={classes.previewContainer}>
          {files.map((item, index) => {
            const preview = item.file.type.startsWith("image/") ? URL.createObjectURL(item.file) : null

            return (
              <div key={`${item.file.name}-${index}`} className={classes.previewItem}>
                {preview ? (
                  <img src={preview} alt={item.file.name} className={classes.previewImage} />
                ) : (
                  <Typography className={classes.fileName}>
                    <File size={15} /> {item.file.name}
                  </Typography>
                )}

                {item.uploading && <CircularProgress size={16} />}

                {item.error && <Typography color="error">{t("messageComposer.uploadFailedLabel")}</Typography>}

                <IconButton
                  size="small"
                  aria-label={t("messageComposer.removeAttachmentButtonLabel")}
                  onClick={() => removeFile(index)}
                >
                  <X size={14} />
                </IconButton>
              </div>
            )
          })}
        </div>
      )}
      <div className={classes.inputContainer}>
        <TextField
          className={classes.input}
          placeholder={t("messageComposer.placeholder")}
          variant="outlined"
          fullWidth
          size="small"
          multiline
          maxRows={6}
          value={content}
          onChange={handleContentChange}
          disabled={!chatId}
          inputRef={inputRef}
        />

        <IconButton
          className={`${classes.action} ${classes.emojiAction}`}
          disabled={!chatId}
          aria-label={t("emojiButtonLabel")}
          onClick={(event) => toggleEmojiPicker(event.currentTarget)}
        >
          <Smile size={20} />
        </IconButton>

        <EmojiPicker anchorEl={emojiAnchor} onSelect={insertEmoji} onClose={closeEmojiPicker} />

        <MentionPicker
          anchorEl={mentionMatches.length > 0 ? inputRef.current : null}
          members={mentionMatches}
          activeIndex={activeMention}
          onSelect={insertMention}
          onClose={() => setMention(null)}
        />

        <input id="chat-file-upload" type="file" disabled={!chatId} multiple hidden onChange={handleFileSelect} />

        <label htmlFor="chat-file-upload">
          <IconButton
            className={classes.action}
            component="span"
            disabled={!chatId}
            aria-label={t("messageComposer.attachFilesButtonLabel")}
          >
            <Paperclip size={20} />
          </IconButton>
        </label>

        <IconButton
          className={classes.action}
          color="primary"
          aria-label={t("messageComposer.sendButtonLabel")}
          onClick={handleSend}
          disabled={
            !chatId || sending || files.some((file) => file.uploading) || (!content.trim() && files.length === 0)
          }
        >
          {sending ? <CircularProgress size={20} /> : <Send size={20} />}
        </IconButton>
      </div>
    </Paper>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,

    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },

  inputContainer: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(1),

    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(0.5),
    },
  },

  // Without this the text field keeps its content width and the buttons push off screen.
  input: {
    flex: 1,
    minWidth: 0,
  },

  action: {
    flexShrink: 0,

    // Full-size buttons leave almost no room for the text on a 320px screen.
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.75),
    },
  },

  // Phone keyboards have their own emoji key, so the button can give its width to the input.
  emojiAction: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },

  replyPreview: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: 8,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    background: theme.palette.grey[100],
  },

  replyPreviewText: {
    flex: 1,
    minWidth: 0,
  },

  replyPreviewTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.primary.main,
  },

  replyPreviewContent: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  previewContainer: {
    display: "flex",
    gap: theme.spacing(1),
    overflowX: "auto",
  },

  previewItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.grey[100],
  },

  previewImage: {
    width: 50,
    height: 50,
    borderRadius: 6,
    objectFit: "cover",
  },

  fileName: {
    maxWidth: 150,
    fontSize: 13,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    alignContent: "center",
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(0.5),
  },
}))

export default MessageComposer
